import React, { useEffect, useMemo, useRef } from "react";
import { Download, FileText, Play, Trash2 } from "lucide-react";
import { MediaRecord } from "../../../../../../interfaces/MediaObject";
import mediaUtility from "../../../../../../services/utility/MediaUtility";
import "./styles/MediaTile.css";

interface MediaTileProps {
  record: MediaRecord;
  /** Opens the item in the full-screen viewer. */
  onOpen: () => void;
  /** When provided, renders a delete affordance (owners / collaborators). */
  onDelete?: () => void;
  /**
   * When provided, renders a download affordance (owners / collaborators
   * only — gated by the caller, e.g. NineFinds Digital Assets images).
   * Opposite corner from onDelete so both can show at once.
   */
  onDownload?: () => void;
  /** Accessible label, e.g. "Gallery item 3". */
  label?: string;
}

/**
 * A single gallery tile. Renders by media kind:
 *  - image → cover thumbnail
 *  - video → muted frame with a play badge
 *  - pdf   → document card with the file name
 */
const MediaTile: React.FC<MediaTileProps> = ({ record, onOpen, onDelete, onDownload, label }) => {
  const kind = mediaUtility.kindFromContentType(record.mediaType);
  const ariaLabel = label ?? `Open ${mediaUtility.labelForKind(kind).toLowerCase()}`;
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // In-view preview is for touch devices (Instagram-style grid); desktop
  // keeps the static first frame. Computed once — device class doesn't change.
  const canAutoPreview = useMemo(
    () => window.matchMedia?.("(hover: none) and (pointer: coarse)").matches ?? false,
    []
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !canAutoPreview || typeof IntersectionObserver === "undefined") {
      return;
    }
    // Muted looped preview while ≥60% of the tile is on screen; pause (and
    // keep the current frame) once it scrolls out. play() can reject (e.g.
    // low-power mode) — the tile just stays a static frame in that case.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.6 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [canAutoPreview]);

  return (
    <div className="media-tile">
      <button type="button" className="media-tile__surface" onClick={onOpen} aria-label={ariaLabel}>
        {kind === "image" && (
          <img className="media-tile__media" src={record.thumbnailUrl ?? record.publicUrl} alt={label ?? "Gallery image"} loading="lazy" />
        )}

        {kind === "video" && (
          <>
            <video
              ref={videoRef}
              className="media-tile__media"
              src={record.publicUrl}
              muted
              loop
              playsInline
              preload="metadata"
            />
            <span className="media-tile__play" aria-hidden>
              <Play size={20} fill="currentColor" />
            </span>
          </>
        )}

        {kind === "pdf" && (
          <div className="media-tile__doc">
            <FileText size={30} strokeWidth={1.5} />
            <span className="media-tile__doc-name">
              {mediaUtility.fileNameFromPath(record.objectPath)}
            </span>
          </div>
        )}

        {kind !== "image" && (
          <span className="media-tile__type-badge">{mediaUtility.labelForKind(kind)}</span>
        )}
      </button>

      {onDownload && (
        <button
          type="button"
          className="media-tile__download"
          onClick={(e) => {
            e.stopPropagation();
            onDownload();
          }}
          aria-label="Download item"
        >
          <Download size={14} />
        </button>
      )}

      {onDelete && (
        <button
          type="button"
          className="media-tile__delete"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label="Delete item"
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
};

export default MediaTile;
